import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle2, Shield, Clock, Sparkles, Users } from 'lucide-react';
import JobastiLogo from '../components/JobastiLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const HERO_BG = 'linear-gradient(180deg, #2E6BF2 0%, #2563EB 48%, #1D4ED8 100%)';
const BLUE = '#2563EB';
const BLUE_DEEP = '#1D4ED8';

const BENEFITS = [
  { icon: Sparkles, title: 'See it on your roles', desc: 'We tailor the walkthrough to the jobs you are hiring for right now.' },
  { icon: Clock, title: '30 minutes, no pressure', desc: 'A focused tour of matching, video interviews and your pipeline.' },
  { icon: Users, title: 'Meet the team', desc: 'Talk to the people who will help you get set up and hiring fast.' },
];

const CompanyDemo = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', company: '', email: '', size: '11-50', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submitLead = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError(t('companyContact.errorFill'));
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/contact-sales`, form);
      setSubmitted(true);
    } catch (e) {
      setError(null);
      window.location.href = `mailto:hello@jobasti.com?subject=Demo request — ${encodeURIComponent(form.company)}&body=${encodeURIComponent(`Name: ${form.name}\nCompany: ${form.company}\nSize: ${form.size}\nEmail: ${form.email}\n\n${form.message}`)}`;
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'h-11 mt-1.5 w-full bg-white border border-gray-200 rounded-lg px-3 text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-shadow';

  return (
    <div className="antialiased text-slate-800 bg-white selection:bg-pink-200 selection:text-pink-900" style={{ fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif" }} data-testid="company-demo">
      {/* NAV (kept consistent with landing) */}
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="cd-logo">
            <img src="/brand-flame.png" alt="JobastiIQ" className="h-10 w-auto" />
            <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 border border-blue-100 px-2 py-1 rounded-full">
              {t('companyHero.forHire')}
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-9 lg:gap-10 text-[17px] text-slate-700 font-medium">
            <Link to="/" className="hover:text-slate-900 transition-colors">{t('companyNav.features')}</Link>
            <Link to="/#about" className="hover:text-slate-900 transition-colors">{t('companyNav.why')}</Link>
            <Link to="/demo" className="hover:text-slate-900 transition-colors">{t('companyNav.contact')}</Link>
            <Link to="/for-jobseekers" className="hover:text-slate-900 transition-colors">{t('companyNav.forJobSeekers')}</Link>
          </nav>
          <div className="flex items-center gap-3 lg:gap-4">
            <LanguageSwitcher />
            <Link
              to="/login"
              onClick={() => { try { sessionStorage.setItem('jobasti_landing_source', 'companies'); } catch { /* noop */ } }}
              className="hidden md:inline-flex items-center rounded-full border border-slate-300 px-6 py-2.5 text-[15px] font-semibold text-slate-800 hover:border-slate-400 hover:bg-slate-50 transition-colors"
              data-testid="cd-login-btn"
            >
              {t('common.login')}
            </Link>
            <Link to="/demo" className="hidden md:inline-flex items-center text-white px-6 py-2.5 rounded-full text-[15px] font-semibold transition-opacity hover:opacity-90" style={{ backgroundColor: BLUE }} data-testid="cd-nav-cta">
              {t('common.requestDemo')}
            </Link>
          </div>
        </div>
      </header>

      {/* HERO band */}
      <section className="relative text-white pt-20 pb-40 overflow-hidden" style={{ background: HERO_BG }} data-testid="cd-hero">
        <div className="container mx-auto px-6 relative z-10 text-center max-w-3xl">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-[1px] w-10 bg-pink-300/80"></div>
            <span className="text-pink-200 font-semibold tracking-[0.3em] text-xs uppercase">Request a demo</span>
            <div className="h-[1px] w-10 bg-pink-300/80"></div>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.05] mb-6">
            See <span className="font-bold italic notranslate" translate="no">JobastiIQ</span> in action
          </h1>
          <p className="text-lg lg:text-xl text-blue-50/90 leading-relaxed max-w-2xl mx-auto">
            Tell us about your team and the roles you are hiring for, and we&rsquo;ll set up a walkthrough tailored to you.
          </p>
        </div>
      </section>

      {/* CONTENT: benefits + form */}
      <section className="bg-white pb-24">
        <div className="container mx-auto px-6 grid lg:grid-cols-12 gap-12 -mt-10 relative z-10">
          {/* Benefits */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-[1px] w-12 bg-pink-300"></div>
              <span className="text-pink-500 font-bold tracking-[0.2em] text-xs uppercase">What to expect</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-serif font-medium text-navy-900 mb-8">
              A tour built around <span className="italic">your hiring</span>
            </h2>
            <div className="space-y-6">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-pink-100 text-pink-500 flex items-center justify-center flex-shrink-0">
                    <b.icon size={22} strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-navy-900 font-bold">{b.title}</p>
                    <p className="text-gray-600 text-sm mt-0.5 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 pt-8 border-t border-gray-100">
              <p className="text-sm text-gray-500">Prefer email? Reach us at <a href="mailto:hello@jobasti.com" className="text-pink-500 font-semibold hover:underline">hello@jobasti.com</a></p>
            </div>
          </div>

          {/* Form card */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl border border-gray-200 shadow-[0_30px_60px_-30px_rgba(6,11,61,0.35)] p-7 sm:p-10">
              {submitted ? (
                <div className="text-center py-10" data-testid="cd-contact-sent">
                  <CheckCircle2 className="w-14 h-14 mx-auto text-pink-500 mb-4" />
                  <h3 className="text-2xl font-serif font-medium text-navy-900">{t('companyContact.sentTitle')}</h3>
                  <p className="text-sm text-gray-500 mt-2">{t('companyContact.sentBody')}</p>
                  <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-navy-900 text-white px-7 py-3 rounded-full font-bold hover:bg-pink-500 transition-all duration-300 group">
                    Back to home <ArrowRight size={18} className="text-pink-500 group-hover:text-white transition-colors" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={submitLead} className="space-y-5" data-testid="cd-contact-form">
                  <h3 className="text-2xl font-serif font-medium text-navy-900 mb-2">Book your walkthrough</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cd-name" className="text-sm text-gray-600 font-medium">{t('companyContact.yourName')}</label>
                      <input id="cd-name" name="name" value={form.name} onChange={handle} className={input} data-testid="cd-name" />
                    </div>
                    <div>
                      <label htmlFor="cd-company" className="text-sm text-gray-600 font-medium">{t('companyContact.company')}</label>
                      <input id="cd-company" name="company" value={form.company} onChange={handle} className={input} data-testid="cd-company" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cd-email" className="text-sm text-gray-600 font-medium">{t('companyContact.workEmail')}</label>
                      <input id="cd-email" name="email" type="email" value={form.email} onChange={handle} className={input} data-testid="cd-email" />
                    </div>
                    <div>
                      <label htmlFor="cd-size" className="text-sm text-gray-600 font-medium">{t('companyContact.teamSize')}</label>
                      <select id="cd-size" name="size" value={form.size} onChange={handle} className={input} data-testid="cd-size">
                        <option>1-10</option>
                        <option>11-50</option>
                        <option>51-200</option>
                        <option>201-1000</option>
                        <option>1000+</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="cd-msg" className="text-sm text-gray-600 font-medium">{t('companyContact.rolesLabel')}</label>
                    <textarea id="cd-msg" name="message" value={form.message} onChange={handle} rows={3}
                      className="w-full mt-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      data-testid="cd-message" />
                  </div>
                  {error && <p className="text-sm text-red-500">{error}</p>}
                  <button type="submit" disabled={submitting}
                    className="w-full h-12 bg-navy-900 text-white font-bold rounded-full flex items-center justify-center gap-2 hover:bg-pink-500 transition-all duration-300 group"
                    data-testid="cd-submit">
                    {submitting ? t('common.sending') : t('common.requestDemo')}
                    {!submitting && <ArrowRight className="w-4 h-4 text-pink-500 group-hover:text-white transition-colors" />}
                  </button>
                  <p className="text-[11px] text-gray-400 text-center pt-1">
                    {t('companyContact.privacy')} <Shield className="inline w-3 h-3 -mt-0.5" />
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-white py-10 border-t border-white/10" style={{ backgroundColor: BLUE_DEEP }}>
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-blue-100/70">
          <JobastiLogo mono height={24} />
          <p>&copy; {new Date().getFullYear()} <span className="notranslate" translate="no">JobastiIQ</span>. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CompanyDemo;
